import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  SearchBox,
  Spinner,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  CheckmarkRegular,
  CopyRegular,
  DeleteRegular,
  MailRegular,
  MoreHorizontalRegular,
  PeopleTeamRegular,
  PersonAvailableRegular,
  PersonProhibitedRegular,
  PlugConnectedRegular,
  ShieldKeyholeRegular,
} from '@fluentui/react-icons';
import { ROLES, type Role } from '@tvmf/shared';
import { api } from '../../api';
import { useAuth } from '../../auth';
import { DataTable } from '../../components/DataTable';
import { Page } from '../../components/Page';
import { LoadError } from '../../components/records';
import { useDebounced } from '../../hooks/useDebounced';
import { useNotify } from '../../hooks/useNotify';
import { useSort } from '../../lib/sort';
import { DangerText, MutedText } from '../../components/ui';
import { LinkIdentityDialog } from './LinkIdentityDialog';

const useLocalStyles = makeStyles({
  danger: { color: tokens.colorPaletteRedForeground1 },
  blockMt8Danger: { color: tokens.colorPaletteRedForeground1, display: "block", marginTop: '8px' },
  dangerFill: { backgroundColor: tokens.colorPaletteRedBackground3 },
  minW160: { minWidth: '160px' },
  filledPad2px6: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", backgroundColor: tokens.colorNeutralBackground3, ...shorthands.padding("2px", "6px"), ...shorthands.borderRadius("4px") },
  maxW780: { maxWidth: '780px' },
  flexWrapItemsEndGap12: { display: "flex", ...shorthands.gap('12px'), flexWrap: "wrap", alignItems: "end" },
  minW220: { minWidth: '220px' },
  minW200: { minWidth: '200px' },
  maxW320: { maxWidth: '320px' },
  maxW640: { maxWidth: '640px' },
  gridGap14MinW520: { display: "grid", ...shorthands.gap('14px'), minWidth: '520px' },
  flexWrapItemsCenterGap8: { display: "flex", ...shorthands.gap('8px'), alignItems: "center", flexWrap: "wrap" },
  minW140: { minWidth: '140px' },
  minW190: { minWidth: '190px' },
  flexWrapItemsEndGap8: { display: "flex", ...shorthands.gap('8px'), alignItems: "end", flexWrap: "wrap" },
});


const useStyles = makeStyles({
  actions: { display: 'flex', ...shorthands.gap('4px'), flexWrap: 'nowrap' },
});

interface UserRow {
  id: string;
  email: string;
  display_name: string;
  role: string;
  status: string;
  totp_enrolled: boolean;
  msp_id: string | null;
  break_glass?: boolean;
  /** False for an account that exists only through a single sign-on provider. */
  has_password?: boolean;
  ssoProviders?: string[];
  tenants?: { id: string; name: string }[];
}
interface TenantRow {
  id: string;
  name: string;
}
interface MspRow {
  id: string;
  name: string;
}
interface SiteRow {
  id: string;
  sitecode: string;
  name: string | null;
}
interface Membership {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  siteIds: string[];
}

/**
 * Copy-to-clipboard that also works outside a secure context (e.g. the app
 * reached over plain http on the LAN), where `navigator.clipboard` is undefined.
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked - the password is still selectable in the banner */
    }
  };

  return (
    <Button
      size="small"
      icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
      onClick={() => void copy()}
    >
      {copied ? 'Copied' : 'Copy password'}
    </Button>
  );
}

const useSites = (tenantId: string) =>
  useQuery({
    queryKey: ['tenant-sites', tenantId],
    enabled: !!tenantId,
    queryFn: () => api<SiteRow[]>(`/tenants/${tenantId}/sites`),
  });

/** Hard-delete an account, behind a confirmation dialog. SUPER_ADMIN only. */
function DeleteUserButton({ user, onDeleted }: { user: UserRow; onDeleted: () => void }) {
  const ls = useLocalStyles();
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => api(`/users/${user.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setOpen(false);
      onDeleted();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <>
      <Button
        size="small"
        appearance="subtle"
        icon={<DeleteRegular />}
        className={ls.danger}
        title="Delete account"
        aria-label="Delete account"
        onClick={() => {
          setErr(null);
          setOpen(true);
        }}
      />
      <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete {user.display_name}?</DialogTitle>
            <DialogContent>
              This permanently removes <b>{user.email}</b>. Their sign-in sessions, two-factor
              enrolment and customer assignments are deleted with the account. This cannot be
              undone.
              {err && (
                <Text className={ls.blockMt8Danger}>{err}</Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                className={ls.dangerFill}
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                Delete account
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

/** The per-user MSP-branding fallback override - see auth.service.ts's resolveMspBranding. */
function MspCell({ user, msps, canEdit }: { user: UserRow; msps: MspRow[]; canEdit: boolean }) {
  const ls = useLocalStyles();
  const qc = useQueryClient();
  const [value, setValue] = useState(user.msp_id ?? '');

  const save = useMutation({
    mutationFn: (mspId: string) =>
      api(`/users/${user.id}/msp`, { method: 'PATCH', body: JSON.stringify({ mspId: mspId || null }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  if (!canEdit) {
    return <Text size={200}>{msps.find((m) => m.id === user.msp_id)?.name ?? '—'}</Text>;
  }

  return (
    <Dropdown
      size="small"
      placeholder="None"
      selectedOptions={value ? [value] : []}
      value={msps.find((m) => m.id === value)?.name ?? ''}
      onOptionSelect={(_, d) => {
        const next = d.optionValue ?? '';
        setValue(next);
        save.mutate(next);
      }}
      className={ls.minW160}
    >
      <Option value="">None</Option>
      {msps.map((m) => (
        <Option key={m.id} value={m.id}>
          {m.name}
        </Option>
      ))}
    </Dropdown>
  );
}

export function AdminUsers() {
  const ls = useLocalStyles();
  const s = useStyles();
  const qc = useQueryClient();
  const { can, me } = useAuth();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('ENGINEER');
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [siteScope, setSiteScope] = useState<'all' | 'sites'>('all');
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [mspId, setMspId] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [manage, setManage] = useState<UserRow | null>(null);
  const [linking, setLinking] = useState<UserRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [invited, setInvited] = useState<{
    email: string;
    tempPassword: string;
    resent: boolean;
  } | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput);
  const users = useQuery({
    queryKey: ['users', search],
    queryFn: () => api<UserRow[]>(`/users${search ? `?q=${encodeURIComponent(search)}` : ''}`),
  });
  const { sorted: sortedUsers, header: uh } = useSort(users.data ?? [], {
    name: (u) => u.display_name,
    email: (u) => u.email,
    role: (u) => u.role,
    customers: (u) => (u.role === 'SUPER_ADMIN' ? 'all' : (u.tenants ?? []).map((t) => t.name).join(', ')),
    status: (u) => u.status,
    mfa: (u) => (u.totp_enrolled ? 'enrolled' : ''),
  });
  const tenants = useQuery({ queryKey: ['tenants'], queryFn: () => api<TenantRow[]>('/tenants') });
  const msps = useQuery({
    queryKey: ['msps'],
    queryFn: () => api<MspRow[]>('/msps'),
    enabled: can('msp:read'),
  });
  const isStaffRole = role === 'ENGINEER' || role === 'PROJECT_MANAGER';

  const scopeTenantId = role === 'CUSTOMER' && tenantIds.length === 1 ? tenantIds[0] : null;
  const sites = useSites(scopeTenantId ?? '');

  const resetForm = () => {
    setEmail('');
    setDisplayName('');
    setTenantIds([]);
    setSiteScope('all');
    setSiteIds([]);
    setMspId('');
    setErr(null);
  };

  const notify = useNotify();
  const create = useMutation({
    mutationFn: () =>
      api<{ email: string; tempPassword: string }>('/users', {
        method: 'POST',
        body: JSON.stringify({
          email,
          displayName,
          role,
          tenantIds: role === 'SUPER_ADMIN' ? undefined : tenantIds,
          siteIds:
            role === 'CUSTOMER' && scopeTenantId && siteScope === 'sites' ? siteIds : undefined,
          mspId: isStaffRole && mspId ? mspId : undefined,
        }),
      }),
    onSuccess: (data) => {
      resetForm();
      setCreating(false);
      setInvited({ email: data.email, tempPassword: data.tempPassword, resent: false });
      notify(`Invitation sent to ${data.email}`);
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  // Disabling an account and resetting two-factor change who can sign in, so they ask first.
  const [confirm, setConfirm] = useState<{ user: UserRow; action: 'disable' | 'reset-mfa' } | null>(null);
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'disable' | 'enable' | 'reset-mfa'; name: string }) =>
      api(`/users/${id}/${action}`, { method: 'POST' }),
    onSuccess: (_d, v) => {
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['users'] });
      notify(v.action === 'disable' ? `${v.name} disabled` : v.action === 'enable' ? `${v.name} enabled` : `Two-factor reset for ${v.name}`, {
        body: v.action === 'reset-mfa' ? 'They will set up two-factor again at their next sign-in.' : undefined,
      });
    },
    onError: (e) => notify('That did not work', { intent: 'error', body: e instanceof Error ? e.message : undefined }),
  });

  // Break-glass: a local Super Admin who keeps password sign-in when a provider switches local passwords off.
  const breakGlass = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean; name: string }) =>
      api(`/users/${id}/break-glass`, { method: 'POST', body: JSON.stringify({ enabled }) }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      notify(v.enabled ? `${v.name} is now a break-glass administrator` : `${v.name} is no longer a break-glass administrator`);
    },
    onError: (e) => notify('That did not work', { intent: 'error', body: e instanceof Error ? e.message : undefined }),
  });

  const resend = useMutation({
    mutationFn: (id: string) =>
      api<{ email: string; tempPassword: string }>(`/users/${id}/resend-invitation`, {
        method: 'POST',
      }),
    onSuccess: (data) => {
      setInvited({ email: data.email, tempPassword: data.tempPassword, resent: true });
      qc.invalidateQueries({ queryKey: ['users'] });
      notify(`Invitation sent to ${data.email}`);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <Page
      platform
      title="Users"
      subtitle="Platform staff and customer users, and which customers they can work in."
      actions={
        <Button appearance="primary" icon={<AddRegular />} onClick={() => setCreating(true)}>
          New user
        </Button>
      }
    >
      {invited && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>
              {invited.resent ? 'New invitation sent' : 'Invitation sent'} to {invited.email}
            </MessageBarTitle>
            Temporary password:{' '}
            <code
              className={ls.filledPad2px6}
            >
              {invited.tempPassword}
            </code>
            . It&rsquo;s in the email; share it another way only if email isn&rsquo;t set up yet.
            Shown once.
          </MessageBarBody>
          <MessageBarActions>
            <CopyButton text={invited.tempPassword} />
            <Button size="small" appearance="subtle" onClick={() => setInvited(null)}>
              Dismiss
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}
      <Dialog open={creating} onOpenChange={(_, d) => setCreating(d.open)}>
        <DialogSurface className={ls.maxW780}>
          <DialogBody>
            <DialogTitle>New user</DialogTitle>
            <DialogContent>
        <div className={ls.flexWrapItemsEndGap12}>
          <Field label="Email">
            <Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} />
          </Field>
          <Field label="Display name">
            <Input value={displayName} onChange={(_, d) => setDisplayName(d.value)} />
          </Field>
          <Field label="Role">
            <Dropdown
              value={role}
              selectedOptions={[role]}
              onOptionSelect={(_, d) => d.optionValue && setRole(d.optionValue as Role)}
            >
              {ROLES.map((r) => (
                <Option key={r} value={r}>
                  {r}
                </Option>
              ))}
            </Dropdown>
          </Field>
          {role !== 'SUPER_ADMIN' && (
            <Field
              label={role === 'CUSTOMER' ? 'Customer (exactly one)' : 'Assigned customers (one or more)'}
            >
              <Dropdown
                multiselect
                placeholder="Select…"
                selectedOptions={tenantIds}
                onOptionSelect={(_, d) => setTenantIds(d.selectedOptions)}
                className={ls.minW220}
              >
                {(tenants.data ?? []).map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.name}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
          {isStaffRole && can('msp:read') && (
            <Field label="MSP override" hint="Fallback only - used when their email domain doesn't match any MSP">
              <Dropdown
                placeholder="None (domain match / default)"
                selectedOptions={mspId ? [mspId] : []}
                value={msps.data?.find((m) => m.id === mspId)?.name ?? ''}
                onOptionSelect={(_, d) => setMspId(d.optionValue ?? '')}
                className={ls.minW200}
              >
                <Option value="">None</Option>
                {(msps.data ?? []).map((m) => (
                  <Option key={m.id} value={m.id}>
                    {m.name}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
          {scopeTenantId && (
            <Field label="Site access" hint="Limit a site contact to specific sites">
              <Dropdown
                value={siteScope === 'all' ? 'Whole customer' : 'Specific sites'}
                selectedOptions={[siteScope]}
                onOptionSelect={(_, d) => setSiteScope((d.optionValue as 'all' | 'sites') ?? 'all')}
                className={ls.minW200}
              >
                <Option value="all">Whole customer</Option>
                <Option value="sites">Specific sites</Option>
              </Dropdown>
            </Field>
          )}
          {scopeTenantId && siteScope === 'sites' && (
            <Field
              label="Sites"
              hint={sites.data && sites.data.length === 0 ? 'This customer has no sites yet' : undefined}
            >
              <Dropdown
                multiselect
                placeholder="Select sites…"
                selectedOptions={siteIds}
                value={siteLabels(siteIds, sites.data ?? [])}
                onOptionSelect={(_, d) => setSiteIds(d.selectedOptions)}
                className={ls.minW220}
              >
                {(sites.data ?? []).map((st) => (
                  <Option key={st.id} value={st.id} text={st.sitecode}>
                    {st.sitecode}
                    {st.name ? ` — ${st.name}` : ''}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
        </div>
        <MutedText size={200}>
          The user is emailed a temporary password and must set their own password, then set up
          two-factor authentication, on first sign-in. Change their customers and site access any
          time with <b>Manage</b> below.
        </MutedText>
        {err && <DangerText>{err}</DangerText>}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button
            appearance="primary"
            disabled={
              !email ||
              !displayName ||
              create.isPending ||
              (!!scopeTenantId && siteScope === 'sites' && siteIds.length === 0)
            }
            onClick={() => create.mutate()}
          >
            Create & invite
          </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Card>
        <SearchBox
          size="small"
          placeholder="Search by name or email…"
          aria-label="Search users"
          value={searchInput}
          onChange={(_, d) => setSearchInput(d.value)}
          className={ls.maxW320}
        />
        {users.isLoading ? (
          <Spinner size="tiny" />
        ) : users.isError ? (
          <LoadError message={(users.error as Error).message} />
        ) : (
          <DataTable size="small" minWidth={1040}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell {...uh('name')}>Name</TableHeaderCell>
                <TableHeaderCell {...uh('email')}>Email</TableHeaderCell>
                <TableHeaderCell {...uh('role')}>Role</TableHeaderCell>
                <TableHeaderCell {...uh('customers')}>Customers</TableHeaderCell>
                {can('msp:read') && <TableHeaderCell>MSP</TableHeaderCell>}
                <TableHeaderCell {...uh('status')}>Status</TableHeaderCell>
                <TableHeaderCell {...uh('mfa')}>MFA</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((u) => {
                const customers =
                  u.role === 'SUPER_ADMIN'
                    ? 'all'
                    : (u.tenants ?? []).map((t) => t.name).join(', ') || '—';
                return (
                <TableRow key={u.id}>
                  <TableCell title={u.display_name}>{u.display_name}</TableCell>
                  <TableCell title={u.email}>{u.email}</TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell title={customers}>{customers}</TableCell>
                  {can('msp:read') && (
                    <TableCell>
                      {u.role === 'ENGINEER' || u.role === 'PROJECT_MANAGER' ? (
                        <MspCell user={u} msps={msps.data ?? []} canEdit={can('user:update')} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  )}
                  <TableCell>{u.status}</TableCell>
                  <TableCell title={u.ssoProviders?.length ? `Can sign in through: ${u.ssoProviders.join(', ')}` : undefined}>
                    {u.totp_enrolled ? 'enrolled' : u.ssoProviders?.length ? 'by provider' : '—'}
                  </TableCell>
                  <TableCell>
                    <div className={s.actions}>
                      {u.role !== 'SUPER_ADMIN' && (
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<PeopleTeamRegular />}
                          title="Manage customers & sites"
                          aria-label="Manage customers & sites"
                          onClick={() => setManage(u)}
                        />
                      )}
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<MoreHorizontalRegular />}
                            title={`More actions for ${u.display_name}`}
                            aria-label={`More actions for ${u.display_name}`}
                          />
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            <MenuItem
                              icon={u.status === 'active' ? <PersonProhibitedRegular /> : <PersonAvailableRegular />}
                              onClick={() =>
                                u.status === 'active'
                                  ? setConfirm({ user: u, action: 'disable' })
                                  : act.mutate({ id: u.id, action: 'enable', name: u.display_name })
                              }
                            >
                              {u.status === 'active' ? 'Disable account' : 'Enable account'}
                            </MenuItem>
                            <MenuItem icon={<ShieldKeyholeRegular />} onClick={() => setConfirm({ user: u, action: 'reset-mfa' })}>
                              Reset two-factor
                            </MenuItem>
                            {can('sso:manage') && (
                              <MenuItem icon={<PlugConnectedRegular />} onClick={() => setLinking(u)}>
                                Link to provider identity…
                              </MenuItem>
                            )}
                            {can('sso:manage') && u.role === 'SUPER_ADMIN' && (u.break_glass || (u.has_password !== false && !u.ssoProviders?.length)) && (
                              <MenuItem
                                icon={<ShieldKeyholeRegular />}
                                disabled={breakGlass.isPending}
                                onClick={() => breakGlass.mutate({ id: u.id, enabled: !u.break_glass, name: u.display_name })}
                              >
                                {u.break_glass ? 'Remove break-glass (local sign-in)' : 'Make break-glass (keeps password sign-in)'}
                              </MenuItem>
                            )}
                            {u.role !== 'SUPER_ADMIN' && u.has_password !== false && (
                              <>
                                <MenuDivider />
                                <MenuItem icon={<MailRegular />} disabled={resend.isPending} onClick={() => resend.mutate(u.id)}>
                                  Resend invitation email
                                </MenuItem>
                              </>
                            )}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                      {can('user:delete') && u.id !== me?.id && (
                        <DeleteUserButton
                          user={u}
                          onDeleted={() => {
                            qc.invalidateQueries({ queryKey: ['users'] });
                            notify(`${u.display_name} deleted`);
                          }}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </DataTable>
        )}
      </Card>

      <Dialog open={!!confirm} onOpenChange={(_, d) => !d.open && setConfirm(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{confirm?.action === 'disable' ? `Disable ${confirm?.user.display_name}?` : `Reset two-factor for ${confirm?.user.display_name}?`}</DialogTitle>
            <DialogContent>
              {confirm?.action === 'disable'
                ? `${confirm.user.email} will be signed out and will not be able to sign in until you enable the account again.`
                : `${confirm?.user.email} will have to set up two-factor again at their next sign-in. Do this only when you have checked it is really them.`}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={act.isPending}
                onClick={() => confirm && act.mutate({ id: confirm.user.id, action: confirm.action, name: confirm.user.display_name })}
              >
                {confirm?.action === 'disable' ? 'Disable account' : 'Reset two-factor'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {linking && <LinkIdentityDialog user={linking} onClose={() => setLinking(null)} />}

      {manage && (
        <UserMembershipsDialog
          user={manage}
          tenants={tenants.data ?? []}
          onClose={() => setManage(null)}
        />
      )}
    </Page>
  );
}

/* --------------------- per-user membership management --------------------- */

function UserMembershipsDialog({
  user,
  tenants,
  onClose,
}: {
  user: UserRow;
  tenants: TenantRow[];
  onClose: () => void;
}) {
  const ls = useLocalStyles();
  const qc = useQueryClient();
  const memberships = useQuery({
    queryKey: ['user-memberships', user.id],
    queryFn: () => api<Membership[]>(`/users/${user.id}/memberships`),
  });
  const refetch = () => {
    qc.invalidateQueries({ queryKey: ['user-memberships', user.id] });
    qc.invalidateQueries({ queryKey: ['users'] });
  };
  const taken = new Set((memberships.data ?? []).map((m) => m.tenantId));

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={ls.maxW640}>
        <DialogBody>
          <DialogTitle>
            {user.display_name} — customer access{' '}
            <Badge appearance="tint" color="informative">
              {user.role}
            </Badge>
          </DialogTitle>
          <DialogContent>
            <div className={ls.gridGap14MinW520}>
              {memberships.isLoading ? (
                <Spinner size="tiny" />
              ) : memberships.isError ? (
                <LoadError message={(memberships.error as Error).message} />
              ) : memberships.data!.length === 0 ? (
                <Text size={200}>Not assigned to any customer yet.</Text>
              ) : (
                <DataTable size="small" minWidth={460}>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Customer</TableHeaderCell>
                      <TableHeaderCell>Site access</TableHeaderCell>
                      <TableHeaderCell />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberships.data!.map((m) => (
                      <MembershipRow key={m.tenantId} user={user} m={m} onChanged={refetch} />
                    ))}
                  </TableBody>
                </DataTable>
              )}
              <AddMembershipRow
                user={user}
                tenants={tenants}
                taken={taken}
                onChanged={refetch}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>
                Close
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

/** Collapsed-display text for a multiselect site Dropdown. */
const siteLabels = (ids: string[], sites: SiteRow[]) =>
  ids
    .map((id) => sites.find((st) => st.id === id)?.sitecode ?? id.slice(0, 6))
    .join(', ');

function MembershipRow({
  user,
  m,
  onChanged,
}: {
  user: UserRow;
  m: Membership;
  onChanged: () => void;
}) {
  const ls = useLocalStyles();
  const isCustomer = user.role === 'CUSTOMER';
  const sites = useSites(isCustomer ? m.tenantId : '');
  const [mode, setMode] = useState<'all' | 'sites'>(m.siteIds.length ? 'sites' : 'all');
  const [ids, setIds] = useState<string[]>(m.siteIds);
  const [err, setErr] = useState<string | null>(null);

  const dirty =
    isCustomer &&
    (mode === 'all' ? m.siteIds.length > 0 : !sameIds(ids, m.siteIds) || m.siteIds.length === 0);

  const save = useMutation({
    mutationFn: () =>
      api(`/tenants/${m.tenantId}/members/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ siteIds: mode === 'sites' ? ids : [] }),
      }),
    onSuccess: () => {
      setErr(null);
      onChanged();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });
  const remove = useMutation({
    mutationFn: () => api(`/tenants/${m.tenantId}/members/${user.id}`, { method: 'DELETE' }),
    onSuccess: onChanged,
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <TableRow>
      <TableCell>{m.tenantName}</TableCell>
      <TableCell>
        {isCustomer ? (
          <div className={ls.flexWrapItemsCenterGap8}>
            <Dropdown
              size="small"
              value={mode === 'all' ? 'Whole customer' : 'Specific sites'}
              selectedOptions={[mode]}
              onOptionSelect={(_, d) => setMode((d.optionValue as 'all' | 'sites') ?? 'all')}
              className={ls.minW140}
            >
              <Option value="all">Whole customer</Option>
              <Option value="sites">Specific sites</Option>
            </Dropdown>
            {mode === 'sites' && (
              <Dropdown
                size="small"
                multiselect
                placeholder="Select sites…"
                selectedOptions={ids}
                value={siteLabels(ids, sites.data ?? [])}
                onOptionSelect={(_, d) => setIds(d.selectedOptions)}
                className={ls.minW190}
              >
                {(sites.data ?? []).map((st) => (
                  <Option key={st.id} value={st.id} text={st.sitecode}>
                    {st.sitecode}
                    {st.name ? ` — ${st.name}` : ''}
                  </Option>
                ))}
              </Dropdown>
            )}
            {dirty && (
              <Button
                size="small"
                appearance="primary"
                disabled={save.isPending || (mode === 'sites' && ids.length === 0)}
                onClick={() => save.mutate()}
              >
                Save
              </Button>
            )}
          </div>
        ) : (
          <Text size={200}>Whole customer</Text>
        )}
        {err && <DangerText size={200}>{err}</DangerText>}
      </TableCell>
      <TableCell>
        <Button
          size="small"
          appearance="subtle"
          icon={<DeleteRegular />}
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
        >
          Remove
        </Button>
      </TableCell>
    </TableRow>
  );
}

function AddMembershipRow({
  user,
  tenants,
  taken,
  onChanged,
}: {
  user: UserRow;
  tenants: TenantRow[];
  taken: Set<string>;
  onChanged: () => void;
}) {
  const ls = useLocalStyles();
  const isCustomer = user.role === 'CUSTOMER';
  const [tid, setTid] = useState('');
  const [mode, setMode] = useState<'all' | 'sites'>('all');
  const [ids, setIds] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const sites = useSites(isCustomer ? tid : '');
  const options = tenants.filter((t) => !taken.has(t.id));

  const add = useMutation({
    mutationFn: () =>
      api(`/tenants/${tid}/members`, {
        method: 'POST',
        body: JSON.stringify({
          userId: user.id,
          siteIds: isCustomer && mode === 'sites' ? ids : [],
        }),
      }),
    onSuccess: () => {
      setTid('');
      setMode('all');
      setIds([]);
      setErr(null);
      onChanged();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  if (isCustomer && taken.size >= 1) {
    return (
      <MutedText size={200}>
        Customer users belong to a single customer. Remove the current one first to move them.
      </MutedText>
    );
  }

  return (
    <div className={ls.flexWrapItemsEndGap8}>
      <Field label="Add to customer">
        <Dropdown
          placeholder={options.length ? 'Select…' : 'All customers assigned'}
          selectedOptions={tid ? [tid] : []}
          value={options.find((t) => t.id === tid)?.name ?? ''}
          onOptionSelect={(_, d) => {
            setTid(d.optionValue ?? '');
            setIds([]);
            setMode('all');
          }}
          className={ls.minW220}
        >
          {options.map((t) => (
            <Option key={t.id} value={t.id}>
              {t.name}
            </Option>
          ))}
        </Dropdown>
      </Field>
      {isCustomer && tid && (
        <>
          <Field label="Site access">
            <Dropdown
              value={mode === 'all' ? 'Whole customer' : 'Specific sites'}
              selectedOptions={[mode]}
              onOptionSelect={(_, d) => setMode((d.optionValue as 'all' | 'sites') ?? 'all')}
              className={ls.minW160}
            >
              <Option value="all">Whole customer</Option>
              <Option value="sites">Specific sites</Option>
            </Dropdown>
          </Field>
          {mode === 'sites' && (
            <Field label="Sites">
              <Dropdown
                multiselect
                placeholder="Select sites…"
                selectedOptions={ids}
                value={siteLabels(ids, sites.data ?? [])}
                onOptionSelect={(_, d) => setIds(d.selectedOptions)}
                className={ls.minW200}
              >
                {(sites.data ?? []).map((st) => (
                  <Option key={st.id} value={st.id} text={st.sitecode}>
                    {st.sitecode}
                    {st.name ? ` — ${st.name}` : ''}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
        </>
      )}
      <Button
        appearance="primary"
        disabled={!tid || add.isPending || (isCustomer && mode === 'sites' && ids.length === 0)}
        onClick={() => add.mutate()}
      >
        Add
      </Button>
      {err && <DangerText size={200}>{err}</DangerText>}
    </div>
  );
}
